import { useMemo } from "react";
import { useParams, Link } from "react-router";
import { useOperatorTrend } from "../hooks/useOperatorTrend";
import {
  OperatorTrendChart,
  type OperatorTrendMetric,
} from "../components/OperatorTrendChart";
import "./OperatorDetailPage.css";

/**
 * Phase 18 WU-18.E3 — Operator detail drill-in at
 * `/analytics/operators/:id`.
 *
 * Displays a 30-day trend chart (the hook will default the range to the last
 * 30 days when from/to are absent) and a full metric table. Access control
 * is enforced server-side: `analytics.view_own` for self or
 * `analytics.view_others` for anyone else (the /operator-trend route
 * returns 403 otherwise).
 */
export function OperatorDetailPage() {
  const { id } = useParams();
  const operatorId = id ?? "";

  const trend = useOperatorTrend(operatorId);
  const metric: OperatorTrendMetric = "avgKpm";

  const series = useMemo(() => {
    const rows = trend.data?.data ?? [];
    if (rows.length === 0) return [];
    return [
      {
        operatorId,
        operatorName: operatorId,
        points: rows,
      },
    ];
  }, [trend.data, operatorId]);

  const totalDocs = series[0]?.points.reduce((sum, p) => sum + p.docCount, 0) ?? 0;
  const avgKpm = series[0]?.points.length
    ? series[0]!.points.reduce((a, b) => a + b.avgKpm, 0) / series[0]!.points.length
    : 0;

  return (
    <div className="operator-detail">
      <div className="operator-detail__header">
        <Link to="/admin/analytics" className="operator-detail__back">
          ← Analytics
        </Link>
        <h1 className="operator-detail__title">{operatorId}</h1>
      </div>

      {trend.isLoading ? (
        <div className="analytics-loading">Loading…</div>
      ) : trend.error ? (
        <div className="analytics-error">{trend.error}</div>
      ) : (
        <>
          <div className="operator-detail__stats">
            <div className="analytics-stat-card">
              <div className="analytics-stat-value">{totalDocs.toLocaleString()}</div>
              <div className="analytics-stat-label">Total Docs</div>
            </div>
            <div className="analytics-stat-card">
              <div className="analytics-stat-value">{avgKpm.toFixed(1)}</div>
              <div className="analytics-stat-label">Avg KPM</div>
            </div>
            <div className="analytics-stat-card">
              <div className="analytics-stat-value">{series[0]?.points.length ?? 0}</div>
              <div className="analytics-stat-label">Active Days</div>
            </div>
          </div>

          <div className="analytics-section-title">30-Day Trend</div>
          <OperatorTrendChart series={series} metric={metric} />

          <div className="analytics-section-title">Daily Rollups</div>
          {series[0]?.points.length ? (
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Docs</th>
                  <th>Avg KPM</th>
                  <th>Correction Rate</th>
                  <th>Avg Time / Field</th>
                </tr>
              </thead>
              <tbody>
                {series[0].points.map((p) => (
                  <tr key={p.day}>
                    <td>{p.day}</td>
                    <td>{p.docCount.toLocaleString()}</td>
                    <td>{p.avgKpm.toFixed(1)}</td>
                    <td>{(p.correctionRate * 100).toFixed(1)}%</td>
                    <td>
                      {p.avgTimePerFieldMs > 0
                        ? `${Math.round(p.avgTimePerFieldMs)}ms`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="analytics-empty">No activity in the last 30 days.</div>
          )}
        </>
      )}
    </div>
  );
}
